import { ApiClient, createApiClient } from './api-client';
import { getEnvironmentVariable, getErrorMessage, logError, logInfo, showToast } from '../utils';
import { authInterceptor } from '../auth/auth-interceptor';

/**
 * Base service class for API operations
 */
export class ApiService {
  protected client: ApiClient;

  /**
   * Creates a new ApiService instance
   */
  constructor() {
    this.client = createApiClient({
      baseURL: this.getBaseUrl(),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Setup default interceptors
    this.setupInterceptors();
  }

  /**
   * Setup default interceptors for the API client
   * This method can be overridden by subclasses to add custom interceptors
   */
  protected setupInterceptors(): void {
    // Add request logging
    this.client.addRequestInterceptor(config => {
      logInfo(`Request: ${config.method?.toUpperCase()} ${config.url}`);
      return config;
    });

    // Add authentication token using Keycloak
    this.client.addRequestInterceptor(authInterceptor);

    // Add cache control headers to GET requests
    this.client.addRequestInterceptor(config => {
      if (config.method?.toLowerCase() === 'get') {
        config.headers.set('Cache-Control', 'no-cache, no-store, must-revalidate');
        config.headers.set('Pragma', 'no-cache');
      }
      return config;
    });

    // Add response logging
    this.client.addResponseInterceptor(response => {
      logInfo(`Response: ${response.status} ${response.config.url}`);
      return response;
    });

    this.client.addErrorInterceptor(error => {
      const formattedMessage = getErrorMessage(error);
      logError(`API Error: ${formattedMessage}`, error);
      error.message = formattedMessage;
      return Promise.reject(error);
    });
  }

  protected getBaseUrl(): string {
    return (getEnvironmentVariable('API_BASE_URL') as string) ?? 'http://localhost:3000/api';
  }

  protected async handleApiCall<T>(apiCall: () => Promise<T>, errorContext?: string): Promise<T> {
    try {
      return await apiCall();
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      const contextMessage = errorContext ? `${errorContext}: ${errorMessage}` : errorMessage;
      logError(contextMessage, error);

      showToast(errorMessage, 'error');

      throw new Error(errorMessage);
    }
  }
}
